import { FC, ReactNode, useCallback, useEffect } from "react";
import * as tabs from "@zag-js/tabs";
import { useMachine, normalizeProps } from "@zag-js/react";
import { match } from "ts-pattern";
import { usePrevious } from "../../hooks/use-previous";
import { classNames } from "../../utils/class-names";
import {
  actionContainerClassMap,
  getActionTriggerClassNames,
} from "../../styles/action-trigger-styles";

interface TabsProps<T> {
  id: string;
  value: T | null;
  onChange: (value: T | null) => void;
  data: Tab<T>[];
  variant?: TabVariant;
  tabsContainerClassName?: string;
}

type TabVariant = "normal" | "fixed";

export interface Tab<T> {
  value: T;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  ["data-cy"]?: string;
  "aria-label"?: string;
}

export function Tabs<T extends string>({
  id,
  data,
  value,
  onChange,
  variant = "normal",
  tabsContainerClassName = "",
}: TabsProps<T>) {
  const onTabChange = useCallback(
    (details: { value: string | null }) => {
      /**
       * values are of type T, when they're rendered, therefore it is
       * safe to cast as T here.
       * tabs.machine widens the type to string
       */
      onChange(details.value as T);
    },
    [onChange]
  );

  const [state, send] = useMachine(tabs.machine({ id, value }), {
    context: { onChange: onTabChange },
  });

  const api = tabs.connect(state, send, normalizeProps);
  const { setValue } = api;
  const previousValue = usePrevious(value);

  useEffect(() => {
    if (previousValue !== value) {
      setValue(value!);
    }
    // disabling, since we only want to update the value in the state
    // machine, when the external value updated. The whole point of using
    // previousValue is to check against it. we don't want to be notified
    // of when previousValue changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, setValue]);

  return (
    <div {...api.rootProps} className="relative">
      <div {...api.tablistProps}>
        <div className={tabsContainerClassName}>
          <div
            className={classNames(
              "overflow-auto whitespace-nowrap",
              actionContainerClassMap[variant]
            )}
          >
            {data.map((tab, index) => {
              const isSelected = tab.value === api.value;
              const { disabled } = tab;
              const state = match({ isSelected, disabled })
                .with({ disabled: true }, () => "disabled" as const)
                .with({ isSelected: true }, () => "selected" as const)
                .otherwise(() => "unselected" as const);

              return (
                <button
                  className={getActionTriggerClassNames(variant, state)}
                  data-cy={tab["data-cy"]}
                  key={index}
                  {...api.getTriggerProps({ value: tab.value, disabled })}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {data.map((tab) => (
        <div {...api.getContentProps({ value: tab.value })} key={tab.value}>
          <div>{tab.content}</div>
        </div>
      ))}
    </div>
  );
}

interface PulseTabsProps {
  variant?: TabVariant;
  tabs: ReactNode[];
}

export const PulseTabs: FC<PulseTabsProps> = ({ variant = "normal", tabs }) => {
  return (
    <div className={pulseTabContainerClassMap[variant]}>
      {tabs.map((tab, index) => (
        <div
          className={`border border-shale-700 rounded-lg font-medium bg-shale-700 animate-pulse ${pulseTabClassMap[variant]}`}
          key={index}
        >
          <span className="invisible">{tab}</span>
        </div>
      ))}
    </div>
  );
};

const pulseTabClassMap: Record<TabVariant, string> = {
  normal: "text-sm sm:text-base lg:text-lg p-2.5",
  fixed: "text-base px-5 py-2.5",
};

export const pulseTabContainerClassMap: Record<TabVariant, string> = {
  normal: "flex lg:py-2.5 gap-2.5 lg:gap-7",
  fixed: "flex gap-5",
};
